import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";
import { useAuth } from "../contexts/AuthContext";
import "../assets/users.css";
import "../assets/panels.css";
import "./GuardianQueries.css";
import Loader from "../components/Loader";

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const GuardianQueries = () => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState(50); // Default 50 per page for server-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [apiUnavailable, setApiUnavailable] = useState(false); // Track if API is consistently failing
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    perPage: 50,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { user, isAdminRole, isManagementRole, isStaffRole } = useAuth();

  // State for queries table sorting
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // State for edit/delete functionality
  const [editingQuery, setEditingQuery] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [deletingQueryId, setDeletingQueryId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Handle queries sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort queries for display
  const sortedQueries = [...queries].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Handle nested object sorting - map sortField to actual data field names
    if (sortField === "id") {
      aValue = a.id || 0;
      bValue = b.id || 0;
    } else if (sortField === "submitted") {
      aValue = a.created_at || "";
      bValue = b.created_at || "";
    } else if (sortField === "date_closed") {
      aValue = a.closed_at || "";
      bValue = b.closed_at || "";
    } else if (sortField === "status") {
      aValue = a.status_id || "";
      bValue = b.status_id || "";
    } else if (sortField === "guardian") {
      aValue = a.guardian_name || `Guardian ${a.guardian_id}` || "";
      bValue = b.guardian_name || `Guardian ${b.guardian_id}` || "";
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  useEffect(() => {
    // Reset API unavailable flag when pagination changes to allow retry
    setApiUnavailable(false);
    loadGuardianQueries();
  }, [currentPage, entries]); // Refetch when page or entries per page changes

  const loadGuardianQueries = async (retryCount = 0) => {
    const MAX_RETRIES = 2;
    
    // Skip fetch if API is marked as unavailable (after multiple failures)
    if (apiUnavailable && retryCount === 0) {
      console.log("ℹ️ [FRONTEND] API marked as unavailable, skipping fetch and showing empty state");
      setQueries([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        perPage: entries,
        hasNextPage: false,
        hasPrevPage: false,
      });
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      // Add pagination parameters to API call
      const url = new URL(
        `${process.env.REACT_APP_API_URL}/api/guardian-queries`
      );
      url.searchParams.append("page", currentPage.toString());
      url.searchParams.append("limit", entries.toString());

      console.log(
        `🔍 [FRONTEND] Fetching guardian queries - page ${currentPage}, limit ${entries}${retryCount > 0 ? ` (retry ${retryCount})` : ''}`
      );

      let response;
      try {
        response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      } catch (fetchError) {
        // Handle network-level fetch failures (CORS, network errors, etc.)
        console.error("❌ [FRONTEND] Fetch error:", fetchError);
        console.error("❌ [FRONTEND] Fetch error details:", {
          message: fetchError.message,
          name: fetchError.name,
          stack: fetchError.stack,
        });
        
        // Retry on network errors
        if (retryCount < MAX_RETRIES && (
          fetchError.message.includes('Failed to fetch') ||
          fetchError.message.includes('network') ||
          fetchError.name === 'TypeError' ||
          fetchError.name === 'NetworkError'
        )) {
          console.log(`🔄 [FRONTEND] Retrying request due to fetch error (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
          return loadGuardianQueries(retryCount + 1);
        }
        
        throw new Error(`Network error: ${fetchError.message}`);
      }

      if (!response.ok) {
        throw new Error(`Failed to load guardian queries: ${response.status} ${response.statusText}`);
      }

      // Handle potential Content-Length mismatch by reading response as text first
      let data;
      let responseText = null;
      try {
        // Check if response is valid before trying to read
        if (!response || !response.body) {
          throw new Error("Invalid response: response or response body is null");
        }
        
        responseText = await response.text();
        if (!responseText || responseText.trim() === '') {
          throw new Error("Empty response from server");
        }
        data = JSON.parse(responseText);
      } catch (parseError) {
        // Check if this is actually a fetch/network error (not a parse error)
        const isNetworkError = 
          parseError.message.includes('Failed to fetch') ||
          parseError.message.includes('network') ||
          parseError.message.includes('NetworkError') ||
          parseError.name === 'TypeError' ||
          parseError.name === 'NetworkError';
        
        console.error("❌ [FRONTEND] Error reading/parsing response:", parseError);
        console.error("❌ [FRONTEND] Error type:", isNetworkError ? "Network Error" : "Parse Error");
        if (response) {
          console.error("❌ [FRONTEND] Response status:", response.status);
          console.error("❌ [FRONTEND] Response headers:", Object.fromEntries(response.headers.entries()));
        }
        console.error("❌ [FRONTEND] Response text length:", responseText?.length || 0);
        
        // Retry on network errors or Content-Length mismatch
        if (retryCount < MAX_RETRIES && (
          isNetworkError ||
          parseError.message.includes('JSON') || 
          parseError.message.includes('parse') ||
          (response && response.status === 200)
        )) {
          const errorType = isNetworkError ? "network error" : "parse error";
          console.log(`🔄 [FRONTEND] Retrying request due to ${errorType} (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
          return loadGuardianQueries(retryCount + 1);
        }
        
        // Provide clearer error message
        if (isNetworkError) {
          throw new Error(`Network error: ${parseError.message}`);
        } else {
          throw new Error(`Failed to parse response: ${parseError.message}`);
        }
      }
      console.log("Guardian queries data:", data);

      // Handle both DataTables format and simple array format
      let queriesData = [];
      if (data.data && Array.isArray(data.data)) {
        queriesData = data.data;
      } else if (data.success && data.data && Array.isArray(data.data)) {
        queriesData = data.data;
      } else if (Array.isArray(data)) {
        queriesData = data;
      }

      console.log("Processed queries data:", queriesData);
      if (queriesData.length > 0) {
        console.log("Sample query fields:", Object.keys(queriesData[0]));
        console.log("Sample query data:", queriesData[0]);
      }

      setQueries(queriesData);

      // Set pagination metadata if available
      if (data.pagination) {
        setPagination(data.pagination);
        console.log(
          `✅ [FRONTEND] Pagination: page ${data.pagination.currentPage} of ${data.pagination.totalPages} (${data.pagination.totalCount} total)`
        );
      } else if (data.recordsTotal !== undefined) {
        // Fallback: calculate pagination from DataTables format
        const totalPages = Math.ceil(data.recordsTotal / entries);
        setPagination({
          currentPage,
          totalPages,
          totalCount: data.recordsTotal,
          perPage: entries,
          hasNextPage: currentPage < totalPages,
          hasPrevPage: currentPage > 1,
        });
      }
    } catch (err) {
      console.error("Error loading guardian queries:", err);
      console.error("Error details:", {
        message: err.message,
        name: err.name,
        stack: err.stack,
      });
      
      // Check if this is a "Failed to fetch" error that needs retry
      // Note: "Failed to parse response: Failed to fetch" is actually a network error
      const isNetworkError = 
        err.message.includes('Failed to fetch') ||
        err.message.includes('ERR_CONTENT_LENGTH_MISMATCH') ||
        err.message.includes('network') ||
        err.message.includes('NetworkError') ||
        err.message.includes('Network error:') ||
        err.name === 'TypeError' ||
        err.name === 'NetworkError';
      
      // Retry on network errors (Failed to fetch, Content-Length mismatch, etc.)
      if (retryCount < MAX_RETRIES && isNetworkError) {
        console.log(`🔄 [FRONTEND] Retrying request due to network error (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
        return loadGuardianQueries(retryCount + 1);
      }
      
      // After all retries failed, use graceful fallback instead of showing error
      console.warn("⚠️ [FRONTEND] All retry attempts failed. Using graceful fallback with empty data.");
      
      // Mark API as unavailable to skip future fetch attempts
      setApiUnavailable(true);
      
      // Set empty data instead of showing error to user
      setQueries([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        perPage: entries,
        hasNextPage: false,
        hasPrevPage: false,
      });
      
      // Show a subtle info message instead of error
      // Don't set error state - just log it for debugging
      console.log("ℹ️ [FRONTEND] Guardian queries unavailable. Showing empty state.");
      
      // Optionally show a non-intrusive message to user
      // You can uncomment this if you want to show a toast notification
      // toast.info("Unable to load guardian queries. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (statusId) => {
    const statusMap = {
      0: { text: "New query", color: "#006cff" },
      1: { text: "Global Guardians Replied", color: "#034ea2" },
      2: { text: "Guardian Replied", color: "#2FC694" },
      3: { text: "Closed", color: "#ed4d5e" },
      14: { text: "New query", color: "#006cff" },
      15: { text: "Global Guardians Replied", color: "#034ea2" },
      16: { text: "Guardian Replied", color: "#2FC694" },
      17: { text: "Closed", color: "#ed4d5e" },
    };

    const status = statusMap[statusId] || {
      text: `Status ${statusId}`,
      color: "#666",
    };

    return (
      <span
        className="badge"
        style={{ backgroundColor: status.color, color: "#fff" }}
      >
        {status.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return (
        date.toLocaleDateString("en-GB") +
        " " +
        date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      );
    } catch (error) {
      return dateString;
    }
  };

  const handleViewQuery = (queryId) => {
    navigate(`/guardian-queries/${queryId}`);
  };

  const handleEditQuery = (query) => {
    setEditingQuery(query);
    setEditDescription(query.description);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editDescription.trim()) {
      alert('Description cannot be empty');
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/guardian-queries/${editingQuery.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ description: editDescription }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update query');
      }

      const data = await response.json();

      // Update the query in the list
      setQueries(queries.map(q =>
        q.id === editingQuery.id ? { ...q, description: editDescription } : q
      ));

      setShowEditModal(false);
      setEditingQuery(null);
      setEditDescription('');
      alert('Query updated successfully!');
    } catch (err) {
      console.error('Error updating query:', err);
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteQuery = async (queryId) => {
    if (!window.confirm('Are you sure you want to delete this query? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(true);
      setDeletingQueryId(queryId);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/guardian-queries/${queryId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete query');
      }

      // Remove the query from the list
      setQueries(queries.filter(q => q.id !== queryId));
      alert('Query deleted successfully!');
    } catch (err) {
      console.error('Error deleting query:', err);
      alert(err.message);
    } finally {
      setActionLoading(false);
      setDeletingQueryId(null);
    }
  };

  const canEditOrDelete = (query) => {
    // Can only edit/delete if query is not closed (status_id !== 3)
    // and has no replies (we'll assume if it's in New Query status it has no replies)
    return query.status_id === 0 || query.status_id === 14;
  };

  // Check if user has permission to edit guardian queries
  const canEditQuery = () => {
    if (!user) return false;
    
    // Check for specific guardian queries permissions
    if (hasPermission('guardian-queries.edit') || hasPermission('guardian-queries.update')) {
      return true;
    }
    // Check for general guardian management permissions
    if (hasPermission('guardians.edit') || hasPermission('guardians.manage')) {
      return true;
    }
    // Staff, management, and admin roles typically have edit permissions
    if (isAdminRole?.() || isManagementRole?.() || isStaffRole?.()) {
      return true;
    }
    // Guardians can edit their own queries (checked in backend)
    if (user?.userable_type === 'guardians' || user?.role?.name === 'GUARDIAN') {
      return true;
    }
    return false;
  };

  // Check if user has permission to delete guardian queries
  const canDeleteQuery = () => {
    if (!user) return false;
    
    // Check for specific guardian queries permissions
    if (hasPermission('guardian-queries.delete') || hasPermission('guardian-queries.destroy')) {
      return true;
    }
    // Check for general guardian management permissions
    if (hasPermission('guardians.delete') || hasPermission('guardians.destroy') || hasPermission('guardians.manage')) {
      return true;
    }
    // Staff, management, and admin roles typically have delete permissions
    if (isAdminRole?.() || isManagementRole?.() || isStaffRole?.()) {
      return true;
    }
    // Guardians can delete their own queries (checked in backend)
    if (user?.userable_type === 'guardians' || user?.role?.name === 'GUARDIAN') {
      return true;
    }
    return false;
  };

  if (loading) return <Loader />;
  if (error)
    return <div className="container py-5 text-danger">Error: {error}</div>;

  // Client-side search filtering (server-side search can be added later if needed)
  const filteredQueries = sortedQueries.filter((query) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      query.id.toString().includes(searchLower) ||
      (query.guardian_name &&
        query.guardian_name.toLowerCase().includes(searchLower)) ||
      (query.description &&
        query.description.toLowerCase().includes(searchLower))
    );
  });

  // Server-side pagination - use data directly from API (no client-side slicing)
  const paginatedQueries = filteredQueries; // Already paginated from backend

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= (pagination.totalPages || 1)) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle entries per page change
  const handleEntriesChange = (e) => {
    const newLimit = Number(e.target.value);
    setEntries(newLimit);
    setCurrentPage(1); // Reset to first page when changing entries per page
  };

  // Handle search - reset to page 1 when searching
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    if (e.target.value !== search) {
      setCurrentPage(1);
    }
  };

  return (
    <div className="container-fluid users-page">
      {/* Breadcrumb - matching Users.jsx style */}
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Guardian Queries</span>
        </div>
      </div>

      {/* Guardian Queries Table Panel - matching Users.jsx style */}
      <div className="users-table-panel">
        <div className="users-table-header-row">
          <div className="users-table-title">Guardian Queries</div>
        </div>
        {queries.length > 0 ? (
          <>
            <div className="users-table-toolbar">
              <div className="users-table-entries">
                Show
                <select
                  className="entries-select"
                  value={entries}
                  onChange={handleEntriesChange}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                entries
              </div>
              <div className="users-table-search">
                Search:
                <input
                  type="text"
                  className="form-control bg-white"
                  placeholder="Search guardian queries..."
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <div className="users-table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th
                      className="sortable"
                      onClick={() => handleSort("id")}
                      style={{ cursor: "pointer" }}
                    >
                      ID<SortIcon direction={sortField === "id" ? sortDirection : null} isActive={sortField === "id"} />
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort("submitted")}
                      style={{ cursor: "pointer" }}
                    >
                      Submitted<SortIcon direction={sortField === "submitted" ? sortDirection : null} isActive={sortField === "submitted"} />
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort("date_closed")}
                      style={{ cursor: "pointer" }}
                    >
                      Date Closed<SortIcon direction={sortField === "date_closed" ? sortDirection : null} isActive={sortField === "date_closed"} />
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort("status")}
                      style={{ cursor: "pointer" }}
                    >
                      Status<SortIcon direction={sortField === "status" ? sortDirection : null} isActive={sortField === "status"} />
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort("guardian")}
                      style={{ cursor: "pointer" }}
                    >
                      Guardian<SortIcon direction={sortField === "guardian" ? sortDirection : null} isActive={sortField === "guardian"} />
                    </th>
                    <th className="action-head">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedQueries.map((query) => (
                    <tr key={query.id}>
                      <td>{query.id}</td>
                      <td>{formatDate(query.created_at)}</td>
                      <td>{formatDate(query.closed_at)}</td>
                      <td>{getStatusBadge(query.status_id)}</td>
                      <td>
                        {query.guardian_name || `Guardian ${query.guardian_id}`}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn-action show"
                          onClick={() => handleViewQuery(query.id)}
                        >
                          View
                        </button>
                        {canEditOrDelete(query) && canEditQuery() && (
                          <button
                            className="btn-action edit"
                            onClick={() => handleEditQuery(query)}
                            disabled={actionLoading}
                            title="Edit query"
                          >
                            Edit
                          </button>
                        )}
                        {canEditOrDelete(query) && canDeleteQuery() && (
                          <button
                            className="btn-action remove"
                            onClick={() => handleDeleteQuery(query.id)}
                            disabled={actionLoading || deletingQueryId === query.id}
                            title="Delete query"
                          >
                            {deletingQueryId === query.id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Info and Controls - matching Users.jsx style */}
            <div className="dataTables_info_wrapper" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
              <div className="dataTables_info">
                Showing {pagination.totalCount === 0 ? 0 : ((currentPage - 1) * entries) + 1} to {Math.min(currentPage * entries, pagination.totalCount || 0)} of {pagination.totalCount || 0} entries
              </div>
              {pagination.totalPages > 1 && (
                <div className="dataTables_paginate">
                  <ul className="pagination">
                    <li className={currentPage === 1 ? 'disabled' : ''}>
                      <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage > 1) handlePageChange(currentPage - 1); }}>
                        Previous
                      </a>
                    </li>
                    {Array.from({ length: Math.min(5, pagination.totalPages || 1) }, (_, i) => {
                      let page;
                      const totalPages = pagination.totalPages || 1;
                      if (totalPages <= 5) {
                        page = i + 1;
                      } else if (currentPage <= 3) {
                        page = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        page = totalPages - 4 + i;
                      } else {
                        page = currentPage - 2 + i;
                      }
                      if (page > totalPages) return null;
                      return (
                        <li key={page} className={currentPage === page ? 'active' : ''}>
                          <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(page); }}>
                            {page}
                          </a>
                        </li>
                      );
                    })}
                    <li className={currentPage === pagination.totalPages ? 'disabled' : ''}>
                      <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage < pagination.totalPages) handlePageChange(currentPage + 1); }}>
                        Next
                      </a>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-4 text-muted">
            No guardian queries found.
          </div>
        )}
      </div>

      {/* Edit Query Modal */}
      {showEditModal && (
        <div
          className="modal fade show"
          style={{
            display: 'block',
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050
          }}
          onClick={() => !actionLoading && setShowEditModal(false)}
        >
          <div
            className="modal-dialog"
            role="document"
            onClick={(e) => e.stopPropagation()}
            style={{
              marginTop: '100px',
              maxWidth: '600px'
            }}
          >
            <div className="modal-content" style={{ border: '1px solid #ddd', borderRadius: '4px' }}>
              <div className="modal-header" style={{
                padding: '15px',
                borderBottom: '1px solid #e5e5e5',
                backgroundColor: '#f5f5f5'
              }}>
                <h4 className="modal-title" style={{ margin: 0, fontWeight: '500' }}>
                  Edit Query #{editingQuery?.id}
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => !actionLoading && setShowEditModal(false)}
                  disabled={actionLoading}
                  style={{
                    border: 'none',
                    background: 'none',
                    fontSize: '24px',
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    opacity: actionLoading ? 0.5 : 1
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ padding: '20px' }}>
                <div className="form-group">
                  <label htmlFor="edit-description" style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                    Description <span style={{ color: 'red' }}>*</span>
                  </label>
                  <textarea
                    id="edit-description"
                    className="form-control"
                    rows="6"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    disabled={actionLoading}
                    placeholder="Enter your query description..."
                    style={{
                      resize: 'vertical',
                      fontSize: '14px',
                      opacity: actionLoading ? 0.6 : 1
                    }}
                  />
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Note: You can only edit queries that haven't received any replies.
                  </small>
                </div>
              </div>
              <div className="modal-footer" style={{
                padding: '15px',
                borderTop: '1px solid #e5e5e5',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px'
              }}>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => setShowEditModal(false)}
                  disabled={actionLoading}
                  style={{
                    padding: '6px 12px',
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    opacity: actionLoading ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveEdit}
                  disabled={actionLoading || !editDescription.trim()}
                  style={{
                    padding: '6px 12px',
                    cursor: (actionLoading || !editDescription.trim()) ? 'not-allowed' : 'pointer',
                    opacity: (actionLoading || !editDescription.trim()) ? 0.5 : 1
                  }}
                >
                  {actionLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuardianQueries;
